import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PWAThunk } from 'lib/core/data'
import { Callback, CreateCustomerInput, MagentoCustomerOutput } from 'lib/types'
import { GraphQLError } from 'graphql'

export interface Slice {
  isFetching: boolean
  isSuccess: boolean
  isFailure: boolean
  payload?: MagentoCustomerOutput
  errors?: string[]
}

export const initialState: Slice = {
  isFetching: false,
  isSuccess: false,
  isFailure: false,
  payload: undefined,
  errors: undefined
}

const slice = createSlice({
  name: 'customer/createCustomer',
  initialState,
  reducers: {
    request (state) {
      state.isFetching = true
    },

    success (state, action: PayloadAction<MagentoCustomerOutput>) {
      state.isFetching = false
      state.isSuccess = true
      state.isFailure = false
      state.payload = action.payload
    },

    failure (state, action?: PayloadAction<string[] | undefined>) {
      state.isFetching = false
      state.isSuccess = false
      state.isFailure = true
      state.payload = initialState.payload
      state.errors = action?.payload
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export function createCustomer (input: CreateCustomerInput, callback?: Callback): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    dispatch(request())

    try {
      const response = await magentoClient.getClient().customer.createCustomer(input)
      const data = response.data?.createCustomer
      if (data?.customer.email) {
        dispatch(success(data))
        callback?.onSuccess?.()
      } else {
        const errors = response.errors?.map(e => e.message)
        dispatch(failure(errors))
        callback?.onFailure?.()
      }
      return response
    } catch ({ message, graphQLErrors }) {
      // https://github.com/apollographql/apollo-link/issues/1022#issuecomment-517809093
      const errors = (graphQLErrors as GraphQLError[] | undefined)?.map(e => e.message)
      dispatch(failure(errors))
      callback?.onFailure?.({
        error: new Error(`[Create Customer] ${message}`)
      })
    }
  }
}
